<template>
  <vb-checkbox
    v-if="node.selection_type === 'single'"
    @change="(selected) => $emit('change', { node: node, value: selected })"
    :vb_options="single_options"
  >
    <template v-if="node.tooltip" #default>
      <span
        class="vb-check__title pl-1 d-inline-flex align-items-center align-middle"
      >
        {{ node.label }}
        <vb-icon
          v-b-popover.hover.top="node.tooltip"
          :name="'info'"
          :color="sass('gray-500')"
          :size="16"
          class="ml-1 cursor-pointer"
        />
      </span>
    </template>
  </vb-checkbox>

  <div
    v-else-if="node.selection_type === 'histogram'"
    class="filters__sub-category"
  >
    <p class="filters__section__title d-flex justify-content-between">
      <span class="d-flex align-items-center">
        {{ node.label }}
        <vb-icon
          v-if="node.tooltip"
          v-b-popover.hover.top="node.tooltip"
          :name="'info'"
          :color="sass('gray-500')"
          :size="16"
          class="ml-1 cursor-pointer"
        />
      </span>
      <small>{{ range_label }}</small>
    </p>
    <vb-range-slider
      class="my-4"
      :key="`slider-${node.id}-${widget_key}`"
      @change="on_range_change"
      :vb_options="{
        items: histogram.values,
        inputs: false,
        min: range.min,
        max: range.max,
        min_angle: histogram.min,
        max_angle: histogram.max,
        unbounded: unbounded_range,
      }"
    />
  </div>

  <div v-else class="filters__sub-category">
    <p class="filters__section__title-sub mb-2" v-if="node.label">
      <small>
        <span class="d-flex align-items-center">
          {{ node.label }}
          <vb-icon
            v-if="node.tooltip"
            v-b-popover.hover.top="node.tooltip"
            :name="'info'"
            :color="sass('gray-500')"
            :size="16"
            class="ml-1 cursor-pointer"
          />
        </span>
      </small>
    </p>
    <vb-checkbox
      v-for="(value_item, value_index) in node.values"
      :key="`${node.id}-${value_index}`"
      @change="() => $emit('change', { node: node, value: value_item.value })"
      :vb_options="{
        text: value_item.label,
        value: is_selected(value_item.value),
        type: 'light',
      }"
    />
  </div>
</template>

<script>
  import { format, floor, ceil } from 'mathjs';
  import { is_set } from '@/utilities/commons';
  import { is_value_selected, histogram_settings } from '@/utilities/filters';

  export default {
    name: 'filter-node',

    props: {
      node: {
        type: Object,
        required: true,
      },
      selections: {
        type: Object,
        default: () => ({}),
      },
      single_switch: {
        type: Boolean,
        default: false,
      },
      bucket_transform: {
        type: Function,
        default: null,
      },
      round_bounds: {
        type: Boolean,
        default: false,
      },
      widget_key: {
        type: [String, Number],
        default: 0,
      },
      // slider ends = unlimited bound
      unbounded_range: {
        type: Boolean,
        default: false,
      },
    },

    data() {
      return {
        // live range while dragging, emitted to the parent only on release
        dragging_value: null,
      };
    },

    computed: {
      single_options() {
        if (this.single_switch) {
          return {
            text: this.node.label,
            switch: true,
            value: this.is_selected(),
          };
        }
        return {
          text: this.node.label,
          value: this.is_selected(),
          type: 'light',
        };
      },

      histogram() {
        return histogram_settings(this.node, this.bucket_transform);
      },

      range() {
        const selection = {
          ...(this.selections[this.node.filter_key] || {}),
          ...(this.dragging_value || {}),
        };
        if (this.unbounded_range) {
          return { min: selection.minimum, max: selection.maximum };
        }
        return {
          min:
            selection.minimum !== undefined
              ? selection.minimum
              : this.histogram.min,
          max:
            selection.maximum !== undefined
              ? selection.maximum
              : this.histogram.max,
        };
      },

      range_label() {
        const has_min = is_set(this.range.min);
        const has_max = is_set(this.range.max);
        if (!has_min && !has_max) {
          return this.unbounded_range ? 'Qualsiasi prezzo' : '';
        }
        const min = has_min
          ? this.format_value(
              this.round_bounds ? ceil(this.range.min) : this.range.min
            )
          : null;
        const max = has_max
          ? this.format_value(
              this.round_bounds ? floor(this.range.max) : this.range.max
            )
          : null;
        if (!has_min) return `< ${max}€`;
        if (!has_max) return `${min}€ +`;
        return `${min}€ - ${max}€`;
      },
    },

    watch: {
      selections() {
        this.dragging_value = null;
      },
    },

    methods: {
      is_selected(value) {
        return is_value_selected(this.selections, this.node, value);
      },

      format_value(value) {
        return format(value, { notation: 'fixed', precision: 0 }).replace(
          /\B(?=(\d{3})+(?!\d))/g,
          '.'
        );
      },

      on_range_change(option) {
        const value = {};
        if (option.min !== undefined) value.minimum = option.min;
        if (option.max !== undefined) value.maximum = option.max;
        if (option.dragging) {
          this.dragging_value = { ...this.dragging_value, ...value };
          return;
        }
        this.dragging_value = null;
        this.$emit('change', {
          node: this.node,
          value,
          dragging: false,
        });
      },
    },
  };
</script>
